import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Ban, CalendarClock, Clock3, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { httpRequest } from "@/lib/axios";
import { DisplayError } from "@/utils/display-error";

type EmployeeUnavailability = {
  id: string;
  employeesId: string;
  starts_at: string;
  ends_at: string;
  reason: string | null;
};

type Props = {
  employeeId: string;
  employeeName: string;
};

const EMPTY_FORM = {
  starts_at: "",
  ends_at: "",
  reason: "",
};

export function EmployeeUnavailabilityManager({ employeeId, employeeName }: Props) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const queryKey = ["employee-unavailabilities", employeeId];

  const unavailabilities = useQuery({
    queryKey,
    queryFn: async (): Promise<EmployeeUnavailability[]> => {
      const response = await httpRequest.get(
        `/employee-unavailabilities?employeesId=${employeeId}`
      );

      return response.data.data;
    },
  });

  const createBlock = useMutation({
    mutationFn: () =>
      httpRequest.post("/employee-unavailabilities", {
        employeesId: employeeId,
        ...form,
      }),
    onSuccess: async () => {
      setForm(EMPTY_FORM);
      await queryClient.invalidateQueries({ queryKey });
      toast({
        title: "Agenda protegida",
        description: "O período foi bloqueado para novos agendamentos.",
      });
    },
    onError: (error) => {
      toast({
        title: "Não foi possível bloquear o período",
        description: DisplayError(error, "Revise as datas e tente novamente."),
        variant: "destructive",
      });
    },
  });

  const removeBlock = useMutation({
    mutationFn: (id: string) => httpRequest.delete(`/employee-unavailabilities/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      toast({
        title: "Horário liberado",
        description: "O período voltou a aceitar agendamentos.",
      });
    },
    onError: (error) => {
      toast({
        title: "Não foi possível liberar o horário",
        description: DisplayError(error, "Tente novamente."),
        variant: "destructive",
      });
    },
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    createBlock.mutate();
  }

  const canSubmit = form.starts_at !== "" && form.ends_at !== "";

  return (
    <div className="grid h-full gap-6 overflow-y-auto p-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(360px,1.1fr)]">
      <form
        onSubmit={submit}
        className="h-fit rounded-xl border border-app-border bg-app-surface p-5"
      >
        <div className="mb-5 flex items-start gap-3">
          <div className="rounded-xl bg-brand p-2.5 text-brand-fg">
            <CalendarClock aria-hidden="true" className="size-5" />
          </div>
          <div>
            <h3 className="font-semibold text-app-fg-strong">Bloqueio pontual</h3>
            <p className="mt-1 text-sm leading-5 text-app-fg-muted">
              Reserve férias, reuniões e pausas de {employeeName} sem alterar a jornada semanal.
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="unavailability-start" required>Início</Label>
            <Input
              id="unavailability-start"
              type="datetime-local"
              value={form.starts_at}
              onChange={(event) =>
                setForm((current) => ({ ...current, starts_at: event.target.value }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="unavailability-end" required>Fim</Label>
            <Input
              id="unavailability-end"
              type="datetime-local"
              min={form.starts_at || undefined}
              value={form.ends_at}
              onChange={(event) =>
                setForm((current) => ({ ...current, ends_at: event.target.value }))
              }
            />
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <Label htmlFor="unavailability-reason">Motivo</Label>
          <Input
            id="unavailability-reason"
            placeholder="Ex.: treinamento, férias ou compromisso pessoal"
            maxLength={255}
            value={form.reason}
            onChange={(event) =>
              setForm((current) => ({ ...current, reason: event.target.value }))
            }
          />
        </div>

        <Button
          type="submit"
          className="mt-5 w-full"
          disabled={!canSubmit || createBlock.isLoading}
          isLoading={createBlock.isLoading}
        >
          <Ban aria-hidden="true" />
          Bloquear horário
        </Button>
      </form>

      <section aria-labelledby="unavailability-list-title" className="min-w-0">
        <div className="mb-4 flex items-end justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-app-fg-muted">
              Disponibilidade protegida
            </p>
            <h3 id="unavailability-list-title" className="mt-1 font-display text-lg font-medium text-app-fg-strong">
              Próximos bloqueios
            </h3>
          </div>
          <span className="rounded-full border border-app-border bg-app-surface px-3 py-1 text-xs font-medium tabular-nums text-app-fg-muted">
            {unavailabilities.data?.length ?? 0} períodos
          </span>
        </div>

        {unavailabilities.isLoading ? (
          <div className="space-y-3" aria-label="Carregando bloqueios">
            {[0, 1, 2].map((item) => (
              <div key={item} className="h-20 animate-pulse rounded-xl bg-app-surface-muted" />
            ))}
          </div>
        ) : unavailabilities.isError ? (
          <div role="alert" className="rounded-xl border border-app-danger/30 bg-app-danger/5 p-4 text-sm text-app-danger">
            Não foi possível carregar os bloqueios. Atualize a página para tentar novamente.
          </div>
        ) : unavailabilities.data?.length ? (
          <ol className="relative space-y-3 before:absolute before:bottom-5 before:left-[19px] before:top-5 before:w-px before:bg-app-border">
            {unavailabilities.data.map((block) => (
              <li key={block.id} className="relative flex gap-3">
                <div className="relative z-10 mt-4 flex size-10 shrink-0 items-center justify-center rounded-full border-4 border-app-surface bg-brand-soft text-brand">
                  <Clock3 aria-hidden="true" className="size-4" />
                </div>
                <div className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-xl border border-app-border bg-app-surface p-4 transition-colors hover:border-brand">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-app-fg-strong">
                      {block.reason || "Indisponibilidade"}
                    </p>
                    <p className="mt-1 text-sm tabular-nums text-app-fg-muted">
                      {format(new Date(block.starts_at), "dd MMM, HH:mm", { locale: ptBR })}
                      {" — "}
                      {format(new Date(block.ends_at), "dd MMM, HH:mm", { locale: ptBR })}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remover bloqueio ${block.reason || "sem motivo"}`}
                    className="shrink-0 justify-center text-app-fg-muted hover:bg-app-danger/10 hover:text-app-danger"
                    disabled={removeBlock.isLoading}
                    onClick={() => removeBlock.mutate(block.id)}
                  >
                    <Trash2 aria-hidden="true" />
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <EmptyState
            title="Agenda totalmente disponível"
            description="Quando houver uma folga ou compromisso, crie o bloqueio ao lado. Os agentes também respeitarão esse período."
          />
        )}
      </section>
    </div>
  );
}
